import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronUp } from 'lucide-react';

const Privacidade = () => {
  const navigate = useNavigate();
  const [showScroll, setShowScroll] = useState(false);

  useEffect(() => {
    document.title = 'Política de Privacidade - Ateliê PRO';
    const handleScroll = () => setShowScroll(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-purple-900">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-purple-200 shadow-lg">
        <div className="max-w-4xl mx-auto px-6 py-3 flex justify-between items-center">
          <button onClick={() => navigate(-1)} className="text-purple-600 hover:text-purple-700 font-medium">
            ← Voltar
          </button>
          <span className="text-xs text-gray-500">v4 • 2026-05-07</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Hero */}
        <div className="text-center text-white mb-12">
          <h1 className="text-5xl font-bold mb-2">Política de Privacidade</h1>
          <p className="text-xl text-purple-200">Ateliê PRO</p>
          <p className="text-sm text-gray-300 mt-4">Última atualização: 7 de maio de 2026 | Versão: 2026-05-07-v4</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-2xl p-12 space-y-8 text-gray-900">
          {/* Intro */}
          <div className="border-l-4 border-blue-500 pl-6 py-4">
            <p className="text-lg leading-relaxed mb-4">
              Esta Política de Privacidade descreve como o Ateliê PRO coleta, utiliza, armazena, compartilha e protege dados pessoais em conformidade com a Lei Geral de Proteção de Dados (LGPD), Marco Civil da Internet e legislação aplicável.
            </p>
            <p className="text-lg leading-relaxed">
              Ao utilizar a Plataforma, você declara estar ciente e de acordo com esta Política.
            </p>
          </div>

          <hr className="border-gray-200" />

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Quem Somos</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Ateliê PRO é uma plataforma de gestão online para ateliês e pequenos negócios.</p>
              <p className="font-semibold">Canal de Privacidade e Proteção de Dados:</p>
              <p><a href="mailto:privacidade@ateliepro.com.br" className="text-blue-600 hover:underline">privacidade@ateliepro.com.br</a></p>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Dados Coletados</h2>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold">Poderemos coletar:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>nome;</li>
                <li>email;</li>
                <li>telefone;</li>
                <li>CPF/CNPJ;</li>
                <li>endereço;</li>
                <li>dados da empresa;</li>
                <li>informações de cobrança;</li>
                <li>informações fiscais;</li>
                <li>dados inseridos pelo Usuário na Plataforma.</li>
              </ul>
              <p className="font-semibold mt-4">Também poderemos coletar dados técnicos automaticamente:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>endereço IP;</li>
                <li>navegador;</li>
                <li>sistema operacional;</li>
                <li>páginas acessadas;</li>
                <li>horários de acesso;</li>
                <li>cookies;</li>
                <li>identificadores de sessão;</li>
                <li>logs de utilização.</li>
              </ul>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Dados Inseridos pelo Usuário</h2>
            <div className="space-y-3 text-gray-700">
              <p>O Usuário é responsável pelos dados pessoais de terceiros inseridos na Plataforma, incluindo clientes, funcionários e fornecedores.</p>
              <p>O Usuário declara possuir base legal adequada para utilização e compartilhamento dessas informações.</p>
            </div>
          </section>

          <hr className="border-gray-200" />

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Finalidades do Tratamento</h2>
            <div className="space-y-3 text-gray-700">
              <p>Os dados poderão ser utilizados para:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Prestação dos serviços</li>
                <li>Autenticação de acesso</li>
                <li>Emissão fiscal</li>
                <li>Processamento de pagamentos</li>
                <li>Suporte técnico</li>
                <li>Segurança da Plataforma</li>
                <li>Prevenção de fraude</li>
                <li>Cumprimento de obrigações legais</li>
                <li>Comunicações operacionais</li>
                <li>Melhoria dos serviços</li>
                <li>Estatísticas e análises de uso</li>
              </ul>
              <p>Os dados serão tratados apenas para finalidades legítimas, específicas e compatíveis com a prestação dos serviços oferecidos.</p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Bases Legais</h2>
            <div className="space-y-3 text-gray-700">
              <p>O tratamento poderá ocorrer com base em:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Execução de contrato</li>
                <li>Cumprimento de obrigação legal</li>
                <li>Legítimo interesse</li>
                <li>Consentimento, quando aplicável</li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Compartilhamento de Dados</h2>
            <div className="space-y-3 text-gray-700">
              <p>Poderemos compartilhar informações com parceiros necessários ao funcionamento da Plataforma, incluindo:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>ASAAS</li>
                <li>FocusNFe</li>
                <li>Supabase</li>
                <li>Google Analytics</li>
                <li>Autoridades públicas quando exigido por lei</li>
              </ul>
              <p>Não vendemos dados pessoais.</p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Google Analytics e Cookies</h2>
            <div className="space-y-3 text-gray-700">
              <p>Utilizamos cookies e ferramentas analíticas, incluindo Google Analytics, para compreender padrões de uso e melhorar a experiência da Plataforma.</p>
              <p className="font-semibold">Os cookies poderão incluir:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Cookies essenciais</li>
                <li>Cookies de preferência</li>
                <li>Cookies analíticos</li>
              </ul>
              <p>Ao continuar navegando na Plataforma, você concorda com o uso de cookies conforme esta Política.</p>
              <p>O Usuário poderá configurar o navegador para bloquear cookies, observando que algumas funcionalidades poderão ser afetadas.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Segurança da Informação</h2>
            <div className="space-y-3 text-gray-700">
              <p>Adotamos medidas técnicas e organizacionais razoáveis para proteção de dados, incluindo:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Criptografia</li>
                <li>Controle de acesso</li>
                <li>Autenticação segura</li>
                <li>Backups</li>
                <li>Monitoramento de segurança</li>
              </ul>
              <p>Apesar das medidas adotadas, nenhum sistema é completamente imune a falhas ou ataques cibernéticos.</p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Retenção de Dados</h2>
            <div className="space-y-3 text-gray-700">
              <p>Os dados serão armazenados:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Pelo período necessário para prestação dos serviços</li>
                <li>Conforme obrigações legais</li>
                <li>Para exercício regular de direitos</li>
                <li>Para prevenção de fraude e segurança</li>
              </ul>
              <p>Após o término da relação, os dados poderão ser excluídos, desassociados ou mantidos quando houver obrigação legal aplicável.</p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Direitos do Usuário</h2>
            <div className="space-y-3 text-gray-700">
              <p>Nos termos da LGPD, o Usuário poderá solicitar:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Confirmação de tratamento</li>
                <li>Acesso aos dados</li>
                <li>Correção</li>
                <li>Atualização</li>
                <li>Portabilidade</li>
                <li>Anonimização quando aplicável</li>
                <li>Exclusão</li>
                <li>Revogação de consentimento</li>
              </ul>
              <p>Solicitações poderão ser feitas pelo email: <strong>privacidade@ateliepro.com.br</strong></p>
            </div>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Prazo de Resposta</h2>
            <div className="space-y-3 text-gray-700">
              <p>As solicitações serão analisadas e respondidas dentro dos prazos previstos na legislação aplicável.</p>
            </div>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Transferência Internacional</h2>
            <div className="space-y-3 text-gray-700">
              <p>Alguns dados poderão ser armazenados ou processados fora do Brasil através de fornecedores de infraestrutura tecnológica.</p>
              <p>Nessas hipóteses, adotaremos medidas razoáveis para proteção dos dados pessoais.</p>
            </div>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Menores de Idade</h2>
            <div className="space-y-3 text-gray-700">
              <p>A Plataforma não é destinada a menores de 18 anos sem autorização legal adequada.</p>
              <p>Caso identifiquemos coleta indevida de dados de menores, poderemos remover tais informações.</p>
            </div>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Comunicações</h2>
            <div className="space-y-3 text-gray-700">
              <p>Poderemos enviar:</p>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                <li>Emails operacionais</li>
                <li>Notificações de segurança</li>
                <li>Avisos importantes</li>
                <li>Comunicações relacionadas à conta</li>
                <li>Mensagens de suporte</li>
              </ul>
              <p>Mensagens promocionais poderão ser desativadas pelo Usuário quando aplicável.</p>
            </div>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Alterações desta Política</h2>
            <div className="space-y-3 text-gray-700">
              <p>Esta Política poderá ser atualizada periodicamente.</p>
              <p>Alterações relevantes poderão ser comunicadas por email, notificações na Plataforma ou outros canais oficiais.</p>
              <p>A continuidade de uso da Plataforma após atualização constitui concordância com a nova versão.</p>
            </div>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Disposições Gerais</h2>
            <div className="space-y-3 text-gray-700">
              <p>A eventual invalidade de uma cláusula não afetará as demais disposições desta Política.</p>
              <p>O presente documento deve ser interpretado em conjunto com os Termos de Uso da Plataforma.</p>
            </div>
          </section>

          {/* CTA */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg text-center">
            <p className="font-semibold mb-2">Seus dados são importantes para nós</p>
            <p className="text-sm text-blue-100">Cumprimos integralmente a LGPD e protegemos suas informações.</p>
          </div>
        </div>
      </div>

      {/* Scroll to Top */}
      {showScroll && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 p-3 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all"
        >
          <ChevronUp className="w-6 h-6" />
        </button>
      )}
    </div>
  );
};

export default Privacidade;